import { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';
import {
    Search, ShoppingCart, Package, Users, BarChart3, CreditCard,
    HelpCircle, Play, BookOpen, Lightbulb, Mail, MessageCircle,
    CheckCircle2, Zap, Clock, TrendingUp, Shield, RefreshCw, Tag,
    ChevronRight, Video
} from 'lucide-react';

const VIDEO_TUTORIALS = [
    {
        id: 'v1',
        title: 'Getting Started with Mezgeb POS',
        description: 'A complete walkthrough of the dashboard, navigation, and your first setup steps.',
        duration: '5 min',
        category: 'Beginner',
        categoryColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
        thumb: 'https://img.youtube.com/vi/dD3OEO1AiMM/maxresdefault.jpg',
        embedId: 'dD3OEO1AiMM',
    },
    {
        id: 'v2',
        title: 'Processing Your First Sale',
        description: 'Step-by-step guide to ringing up items, accepting cash and card payments, and printing receipts.',
        duration: '4 min',
        category: 'POS',
        categoryColor: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
        thumb: 'https://img.youtube.com/vi/MJzV2CqEt60/maxresdefault.jpg',
        embedId: 'MJzV2CqEt60',
    },
    {
        id: 'v3',
        title: 'Managing Products & Inventory',
        description: 'How to add products, set categories, configure stock alerts and auto-reorder.',
        duration: '6 min',
        category: 'Products',
        categoryColor: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
        thumb: 'https://img.youtube.com/vi/GqzCGc_9KkM/maxresdefault.jpg',
        embedId: 'GqzCGc_9KkM',
    },
    {
        id: 'v4',
        title: 'Reading Your Sales Reports',
        description: 'Understand your daily revenue, top products, and how to export data for accounting.',
        duration: '5 min',
        category: 'Reports',
        categoryColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
        thumb: 'https://img.youtube.com/vi/jEUzkSmLGDg/maxresdefault.jpg',
        embedId: 'jEUzkSmLGDg',
    },
    {
        id: 'v5',
        title: 'Setting Up Your Team & User Roles',
        description: 'Add cashiers and managers, assign roles, and control who can access what.',
        duration: '3 min',
        category: 'Team',
        categoryColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
        thumb: 'https://img.youtube.com/vi/XgMsRfhJxMk/maxresdefault.jpg',
        embedId: 'XgMsRfhJxMk',
    },
    {
        id: 'v6',
        title: 'Configuring Payments & Tax',
        description: 'Set up card fees, tax rates, and Stripe for card processing — no coding required.',
        duration: '4 min',
        category: 'Payments',
        categoryColor: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
        thumb: 'https://img.youtube.com/vi/CpjGsKueXFk/maxresdefault.jpg',
        embedId: 'CpjGsKueXFk',
    },
];

const PRO_TIPS = [
    {
        icon: Zap,
        color: 'text-amber-500',
        bg: 'bg-amber-500/10',
        title: 'Use keyboard shortcuts',
        tip: 'On the POS screen, press the search bar quickly to find products by name or SKU — no mouse needed for fast checkout.',
    },
    {
        icon: Tag,
        color: 'text-cyan-500',
        bg: 'bg-cyan-500/10',
        title: 'Set a low-stock threshold',
        tip: 'Go to each product and set a "Low Stock Alert" number. You\'ll see warnings before you run out — never lose a sale to an empty shelf.',
    },
    {
        icon: Clock,
        color: 'text-violet-500',
        bg: 'bg-violet-500/10',
        title: 'Check reports every morning',
        tip: 'Spend 2 minutes on the Reports page each morning to see yesterday\'s top sellers, peak hours, and payment method breakdown.',
    },
    {
        icon: Users,
        color: 'text-blue-500',
        bg: 'bg-blue-500/10',
        title: 'Give cashiers minimal access',
        tip: 'Create cashier accounts for your staff. They only see the POS screen — they can\'t change prices or view your finances.',
    },
    {
        icon: TrendingUp,
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        title: 'Track cost price for profits',
        tip: 'Always fill in the "Cost Price" when adding a product. This lets the Reports page calculate your actual profit margin automatically.',
    },
    {
        icon: RefreshCw,
        color: 'text-rose-500',
        bg: 'bg-rose-500/10',
        title: 'Enable auto-reorder for fast movers',
        tip: 'On your top-selling products, enable "Auto Reorder" and link a supplier. The system will generate a purchase order when stock runs low.',
    },
    {
        icon: Shield,
        color: 'text-slate-500',
        bg: 'bg-slate-500/10',
        title: 'Use Time Clock for accountability',
        tip: 'Enable the "Require Clock-In" setting in Settings > System. Employees must clock in before using the POS — you can track hours easily.',
    },
    {
        icon: BarChart3,
        color: 'text-orange-500',
        bg: 'bg-orange-500/10',
        title: 'Apply discounts during peak hours',
        tip: 'Use the discount feature on the POS checkout screen to run happy hour or loyalty specials without changing actual product prices.',
    },
];

const GUIDE_SECTIONS = [
    {
        id: 'getting-started',
        icon: Play,
        title: 'Getting Started',
        description: 'Learn the basics of Mezgeb POS',
        articles: [
            {
                title: 'Your First Sale',
                content: `<h4>Making your first sale is easy:</h4><ol><li><strong>Go to Point of Sale</strong> — Click "Point of Sale" in the sidebar</li><li><strong>Select Products</strong> — Click on products to add them to the cart</li><li><strong>Adjust Quantities</strong> — Use + and - buttons to change quantities</li><li><strong>Click Pay</strong> — Choose Cash or Card payment method</li><li><strong>Complete Sale</strong> — Confirm the payment to generate a receipt</li></ol><p><strong>Tip:</strong> Use the search bar to quickly find products by name or SKU.</p>`
            },
            {
                title: 'Understanding the Dashboard',
                content: `<h4>Your dashboard shows key metrics:</h4><ul><li><strong>Today's Revenue</strong> — Total sales for today</li><li><strong>Total Revenue</strong> — All-time sales total</li><li><strong>Products</strong> — Number of products in your catalog</li><li><strong>Low Stock Alerts</strong> — Products that need restocking</li></ul><p>The sales chart shows your revenue trend over the last 7 days.</p>`
            },
            {
                title: 'Navigation Overview',
                content: `<h4>Main sections of Mezgeb POS:</h4><ul><li><strong>Dashboard</strong> — Overview of your store performance</li><li><strong>Point of Sale</strong> — Process sales and checkouts</li><li><strong>Products</strong> — Manage your product catalog</li><li><strong>Inventory</strong> — Track stock levels and restock</li><li><strong>Sales</strong> — View sales history and receipts</li><li><strong>Reports</strong> — Analytics and business insights</li><li><strong>Settings</strong> — Configure your store (Admin only)</li></ul>`
            }
        ]
    },
    {
        id: 'products',
        icon: Package,
        title: 'Product Management',
        description: 'Add and manage your products',
        articles: [
            {
                title: 'Adding a New Product',
                content: `<h4>To add a product:</h4><ol><li>Go to <strong>Products</strong> in the sidebar</li><li>Click <strong>"Add Product"</strong> button</li><li>Fill in the product details:<ul><li><strong>Name</strong> — Product name (e.g., "Chicken Wings")</li><li><strong>SKU</strong> — Unique identifier (e.g., "CW001")</li><li><strong>Price</strong> — Selling price</li><li><strong>Cost</strong> — Your cost (for profit tracking)</li><li><strong>Stock</strong> — Initial quantity</li><li><strong>Category</strong> — Product category</li></ul></li><li>Click <strong>Save</strong></li></ol>`
            },
            {
                title: 'Categories',
                content: `<h4>Organizing with categories:</h4><p>Categories help organize your products for faster checkout.</p><p><strong>Examples for restaurants:</strong> Appetizers, Main Course, Beverages, Desserts</p><p><strong>Examples for retail:</strong> Electronics, Clothing, Accessories</p><p>You can create custom categories in the Products page by clicking <strong>"Category"</strong>.</p>`
            },
            {
                title: 'Editing & Deleting Products',
                content: `<h4>To edit a product:</h4><ol><li>Go to <strong>Products</strong></li><li>Hover over the product row</li><li>Click the <strong>⋮ menu</strong> → Edit</li><li>Make your changes and click <strong>Update Product</strong></li></ol><h4>To delete a product:</h4><ol><li>Click the <strong>⋮ menu</strong> → Delete</li><li>Confirm the deletion</li></ol><p><strong>Warning:</strong> Deleting a product is permanent.</p>`
            }
        ]
    },
    {
        id: 'inventory',
        icon: ShoppingCart,
        title: 'Inventory Management',
        description: 'Track and manage stock levels',
        articles: [
            {
                title: 'Checking Stock Levels',
                content: `<h4>Monitor your inventory:</h4><ol><li>Go to <strong>Inventory</strong> in the sidebar</li><li>View all products with their current stock levels</li><li>Products with low stock are highlighted in red</li></ol><p>The Dashboard also shows a <strong>Low Stock Alert</strong> count in the top stats bar.</p>`
            },
            {
                title: 'Restocking Products',
                content: `<h4>To add stock:</h4><ol><li>Go to <strong>Inventory</strong></li><li>Find the product</li><li>Click <strong>"Add Stock"</strong></li><li>Enter the quantity received</li><li>Click <strong>Confirm</strong></li></ol><p>Stock changes are logged automatically for tracking.</p>`
            },
            {
                title: 'Auto-Reorder',
                content: `<h4>Set up automatic reordering:</h4><ol><li>Go to <strong>Products</strong> and edit a product</li><li>Toggle on <strong>"Auto-Reorder"</strong></li><li>Select a <strong>Supplier</strong></li><li>Set the <strong>Reorder Quantity</strong></li></ol><p>When stock falls below your threshold, a purchase order is created automatically.</p>`
            }
        ]
    },
    {
        id: 'sales',
        icon: CreditCard,
        title: 'Sales & Payments',
        description: 'Process transactions and view history',
        articles: [
            {
                title: 'Processing a Sale',
                content: `<h4>Complete checkout flow:</h4><ol><li><strong>Add items</strong> — Click products to add to cart</li><li><strong>Review cart</strong> — Check items and quantities on the right panel</li><li><strong>Click Pay</strong> — Opens the payment dialog</li><li><strong>Select payment method:</strong><ul><li><strong>Cash</strong> — Enter amount received, change is calculated automatically</li><li><strong>Card</strong> — Processes via Stripe terminal</li></ul></li><li><strong>Complete</strong> — Receipt is generated and stock is deducted</li></ol>`
            },
            {
                title: 'Applying Discounts & Tips',
                content: `<h4>Discount options at checkout:</h4><ul><li>Click the <strong>Discount</strong> field in the cart</li><li>Enter a dollar amount or percentage</li><li>Discount appears as a line item on the receipt</li></ul><h4>Tip handling:</h4><p>Cashiers can add a tip during the payment dialog. The tip is shown separately on the receipt and in sales reports.</p>`
            },
            {
                title: 'Viewing Sales History',
                content: `<h4>To view past sales:</h4><ol><li>Go to <strong>Sales</strong> in the sidebar</li><li>Browse all transactions in the table</li><li>Click any row to view the full receipt details</li><li>Use the <strong>date filter</strong> to find transactions in a range</li></ol><p>Admins and Managers can delete a sale — this also restores the stock for all items in that sale.</p>`
            }
        ]
    },
    {
        id: 'reports',
        icon: BarChart3,
        title: 'Reports & Analytics',
        description: 'Understand your business performance',
        articles: [
            {
                title: 'Sales Reports',
                content: `<h4>Available reports:</h4><ul><li><strong>Daily Sales</strong> — Revenue by day over a selected period</li><li><strong>Top Products</strong> — Your best-selling items by quantity and revenue</li><li><strong>Sales by Category</strong> — Performance by product category</li><li><strong>Payment Methods</strong> — Cash vs Card breakdown</li></ul><p>Use the date range picker to analyze specific periods like last week or last month.</p>`
            },
            {
                title: 'Understanding Metrics',
                content: `<h4>Key performance indicators:</h4><ul><li><strong>Revenue</strong> — Total money collected from sales</li><li><strong>Transactions</strong> — Number of completed sales</li><li><strong>Average Order Value</strong> — Revenue ÷ Transactions</li><li><strong>Gross Profit</strong> — Revenue minus cost of goods sold (requires cost prices set on products)</li></ul>`
            }
        ]
    },
    {
        id: 'users',
        icon: Users,
        title: 'User Management',
        description: 'Manage staff access (Admin only)',
        articles: [
            {
                title: 'User Roles Explained',
                content: `<h4>Three role levels:</h4><ul><li><strong>Admin</strong> — Full access: settings, users, reports, all features</li><li><strong>Manager</strong> — Access to POS, products, inventory, reports. Cannot change settings or manage users.</li><li><strong>Cashier</strong> — Access to POS and sales history only. Cannot view financials.</li></ul><p>Start with <strong>Cashier</strong> for new hires, then promote to Manager after training.</p>`
            },
            {
                title: 'Adding Team Members',
                content: `<h4>To add a new user:</h4><ol><li>Go to <strong>Settings → Users tab</strong></li><li>Click <strong>"Add User"</strong></li><li>Enter their name, email, and a temporary password</li><li>Select their role (Cashier, Manager, or Admin)</li><li>Click <strong>Create User</strong></li></ol><p>Share their email and password securely — they can update their password after first login.</p>`
            }
        ]
    }
];

const FAQ = [
    { question: "How do I process a refund?", answer: "Currently, refunds should be processed manually through your payment processor (Stripe dashboard for card payments). Delete the sale record in the Sales page to restore inventory — then process the refund in Stripe separately." },
    { question: "Can I use Mezgeb POS offline?", answer: "Mezgeb POS requires an internet connection to sync data. We recommend a stable WiFi connection at your location." },
    { question: "How do I export my sales data?", answer: "Go to Reports, select your date range, and click the Export button to download your data as a CSV file for Excel or Google Sheets." },
    { question: "Is my data secure?", answer: "Yes — we use bank-level encryption for all data. Payment processing is handled securely by Stripe, so we never store card details. All data is backed up automatically." },
    { question: "How do I cancel my subscription?", answer: "You can cancel anytime from Settings > Subscription. Your data remains accessible until the end of your billing period." },
    { question: "Can multiple cashiers use the POS at the same time?", answer: "Yes — each cashier logs in with their own account. Each sale is recorded under the cashier who processed it, making it easy to track staff performance." },
    { question: "How do I change my store name or logo?", answer: "Go to Settings → Business tab. You can update your store name, address, phone, and email there. Logo customization is coming in a future update." },
];

export default function Help() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSection, setSelectedSection] = useState('getting-started');
    const [videoOpen, setVideoOpen] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);

    const currentSection = GUIDE_SECTIONS.find(s => s.id === selectedSection);

    const openVideo = (video) => {
        setActiveVideo(video);
        setVideoOpen(true);
    };

    const filteredFAQ = FAQ.filter(f =>
        f.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredSections = GUIDE_SECTIONS.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.articles.some(a => a.title.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <Layout title="Help & Training">
            <div className="max-w-6xl space-y-10" data-testid="help-page">

                {/* Header */}
                <div>
                    <h1 className="text-3xl font-black mb-2">Help & Training</h1>
                    <p className="text-muted-foreground">Video tutorials, tips, and guides to help you get the most out of Mezgeb POS</p>
                </div>

                {/* Search */}
                <div className="relative max-w-xl">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search tutorials, tips, and FAQs..."
                        className="pl-9 h-11"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="help-search"
                    />
                </div>

                {/* ── VIDEO TUTORIALS ── */}
                <section>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
                            <Video className="h-5 w-5 text-rose-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black">Video Tutorials</h2>
                            <p className="text-sm text-muted-foreground">Watch step-by-step walkthroughs for every feature</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {VIDEO_TUTORIALS.map((video) => (
                            <div
                                key={video.id}
                                className="group cursor-pointer rounded-xl border bg-card overflow-hidden hover:border-cyan-500/50 hover:shadow-md transition-all"
                                onClick={() => openVideo(video)}
                                data-testid={`video-card-${video.id}`}
                            >
                                {/* Thumbnail */}
                                <div className="relative aspect-video bg-slate-900 overflow-hidden">
                                    <img
                                        src={video.thumb}
                                        alt={video.title}
                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center border border-white/20 group-hover:bg-cyan-500/80 group-hover:border-cyan-400 transition-all shadow-lg">
                                            <Play className="h-6 w-6 text-white fill-white ml-1" />
                                        </div>
                                    </div>
                                    <div className="absolute bottom-2 right-2">
                                        <span className="bg-black/80 text-white text-xs font-mono px-2 py-0.5 rounded">{video.duration}</span>
                                    </div>
                                </div>
                                {/* Info */}
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <p className="font-semibold text-sm leading-snug group-hover:text-cyan-500 transition-colors">{video.title}</p>
                                        <Badge className={`text-xs border shrink-0 ${video.categoryColor}`} variant="outline">{video.category}</Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{video.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── PRO TIPS ── */}
                <section>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <Lightbulb className="h-5 w-5 text-amber-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black">Pro Tips</h2>
                            <p className="text-sm text-muted-foreground">Quick wins to get more out of your POS every day</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {PRO_TIPS.map((tip, idx) => (
                            <div key={idx} className="flex items-start gap-4 p-4 rounded-xl border bg-card hover:border-cyan-500/30 transition-colors">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tip.bg}`}>
                                    <tip.icon className={`h-5 w-5 ${tip.color}`} />
                                </div>
                                <div>
                                    <p className="font-semibold text-sm mb-1">{tip.title}</p>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{tip.tip}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── WRITTEN GUIDES ── */}
                <section>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-cyan-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black">Step-by-Step Guides</h2>
                            <p className="text-sm text-muted-foreground">Detailed written instructions for every feature</p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-4 gap-6">
                        {/* Topic nav */}
                        <div className="lg:col-span-1">
                            <Card className="shadow-sm sticky top-4">
                                <CardHeader className="pb-2 pt-4 px-4">
                                    <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Topics</CardTitle>
                                </CardHeader>
                                <CardContent className="p-2">
                                    <div className="space-y-0.5">
                                        {GUIDE_SECTIONS.map(section => (
                                            <button
                                                key={section.id}
                                                onClick={() => setSelectedSection(section.id)}
                                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                                                    selectedSection === section.id
                                                        ? 'bg-cyan-500 text-white font-semibold'
                                                        : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                }`}
                                            >
                                                <section.icon className="h-4 w-4 shrink-0" />
                                                <span>{section.title}</span>
                                                {selectedSection === section.id && <ChevronRight className="h-3 w-3 ml-auto" />}
                                            </button>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Articles */}
                        <div className="lg:col-span-3 space-y-4">
                            {currentSection && (
                                <Card className="shadow-sm">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                                                <currentSection.icon className="h-5 w-5 text-cyan-500" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-base">{currentSection.title}</CardTitle>
                                                <CardDescription>{currentSection.description}</CardDescription>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <Accordion type="single" collapsible className="w-full">
                                            {currentSection.articles.map((article, idx) => (
                                                <AccordionItem key={idx} value={`article-${idx}`}>
                                                    <AccordionTrigger className="text-left font-semibold text-sm">
                                                        {article.title}
                                                    </AccordionTrigger>
                                                    <AccordionContent>
                                                        <div
                                                            className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground [&_strong]:text-foreground [&_h4]:text-foreground [&_h4]:font-semibold [&_h4]:mb-2 [&_ol]:space-y-1 [&_ul]:space-y-1"
                                                            dangerouslySetInnerHTML={{ __html: article.content }}
                                                        />
                                                    </AccordionContent>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── FAQ ── */}
                <section>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                            <HelpCircle className="h-5 w-5 text-violet-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black">Frequently Asked Questions</h2>
                            <p className="text-sm text-muted-foreground">Quick answers to the most common questions</p>
                        </div>
                    </div>

                    <Card className="shadow-sm">
                        <CardContent className="pt-4">
                            <Accordion type="single" collapsible className="w-full">
                                {(searchQuery ? filteredFAQ : FAQ).map((item, idx) => (
                                    <AccordionItem key={idx} value={`faq-${idx}`}>
                                        <AccordionTrigger className="text-left font-semibold text-sm">
                                            {item.question}
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <p className="text-muted-foreground text-sm leading-relaxed">{item.answer}</p>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                                {searchQuery && filteredFAQ.length === 0 && (
                                    <p className="py-6 text-center text-sm text-muted-foreground">No FAQ matches your search.</p>
                                )}
                            </Accordion>
                        </CardContent>
                    </Card>
                </section>

                {/* ── CONTACT ── */}
                <Card className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border-cyan-500/30">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                            <div>
                                <h3 className="font-black text-lg mb-1">Still need help?</h3>
                                <p className="text-muted-foreground text-sm">Our support team typically responds within 24 hours</p>
                            </div>
                            <div className="flex gap-3">
                                <Button variant="outline" className="gap-2" onClick={() => window.open('mailto:support@mezgebpos.com')}>
                                    <Mail className="h-4 w-4" /> Email Support
                                </Button>
                                <Button className="gap-2 bg-cyan-500 hover:bg-cyan-600 text-white" onClick={() => window.open('https://mezgebpos.com/contact')}>
                                    <MessageCircle className="h-4 w-4" /> Live Chat
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Video Player Dialog */}
            <Dialog open={videoOpen} onOpenChange={(open) => { setVideoOpen(open); if (!open) setActiveVideo(null); }}>
                <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-slate-800" data-testid="video-dialog">
                    {activeVideo && (
                        <>
                            <div className="aspect-video w-full">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${activeVideo.embedId}?autoplay=1&rel=0`}
                                    title={activeVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full"
                                />
                            </div>
                            <div className="p-4 bg-card">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="font-bold text-sm">{activeVideo.title}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">{activeVideo.description}</p>
                                    </div>
                                    <Badge className={`text-xs border shrink-0 ${activeVideo.categoryColor}`} variant="outline">{activeVideo.category}</Badge>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
